import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CollectorFrameIcon from '@/assets/all-icons/collector-frame-icon';
import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';

import './index.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const CLOSED_CHEST_URL = `${PUBLIC_URL}/suarte-road-assets/ClosedChest.svg`;
const OPEN_CHEST_URL = `${PUBLIC_URL}/suarte-road-assets/OpenChest.svg`;

const FRAME_THEMES = [
  {
    id: 'frame-starry-moon',
    label: 'Starry Moon',
    base: '#FFB500',
    darker: '#6a4c00'
  },
  {
    id: 'frame-dancing',
    label: 'Dancing on the Grass',
    base: '#FF7900',
    darker: '#6a3300'
  },
  {
    id: 'frame-fountain',
    label: 'Fountain of Delights',
    base: '#FF3B58',
    darker: '#6a1925'
  },
  {
    id: 'frame-swinging',
    label: 'Swinging Dress',
    base: '#FF0097',
    darker: '#6a003f'
  },
  {
    id: 'frame-water-lily',
    label: 'Water Lily Pond',
    base: '#D000C5',
    darker: '#570053'
  },
];

const getFrameThemeById = (id) => FRAME_THEMES.find((theme) => theme.id === id) || null;
const REWARD_FRAME_ENDPOINT = `${config.apis.api.url}/suarteroad/reward-frame`;

export default function SuarteRoadReward() {
  const t = useTranslations('common.profile.suarte_road.reward_page');
  const navigate = useNavigate(); // Initialize navigate function
  const { state, stateHandler } = useStateHandler();

  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(Date.now());
  const [textAnimating, setTextAnimating] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCollectorFrame, setShowCollectorFrame] = useState(false);
  const [showReceivedText, setShowReceivedText] = useState(false);
  const [showCollectorsBadgeText, setShowCollectorsBadgeText] = useState(false);
  const [showRewardDetails, setShowRewardDetails] = useState(false);
  const collectorFrameThemeId = state?.user_session?.user_flags?.collector_frame_theme;
  const initialFrameTheme = getFrameThemeById(collectorFrameThemeId) ?? FRAME_THEMES[0];
  const [selectedFrameTheme, setSelectedFrameTheme] = useState(initialFrameTheme);
  const [isClaimingFrame, setIsClaimingFrame] = useState(false);
  const [hasClaimedFrame, setHasClaimedFrame] = useState(false);
  const [claimError, setClaimError] = useState(null);

  const userImageData = state?.user_session?.user_image;
  const legacyProfileImage = state?.user_session?.profile_image;

  const resolveImageString = (candidate) => {
    if (!candidate) return null;
    if (typeof candidate === 'string') {
      return candidate.trim() || null;
    }
    const lookup = [
      candidate.image,
      candidate.image_id,
      candidate.imageId,
      candidate.image_url,
      candidate.url,
      candidate.secure_url,
      candidate.path,
      candidate.id,
    ];
    return lookup.find((value) => typeof value === 'string' && value.trim().length > 0) || null;
  };

  const profileImageCandidates = [
    resolveImageString(userImageData?.cropped_image),
    resolveImageString(userImageData?.image_with_crop),
    resolveImageString(userImageData),
    resolveImageString(legacyProfileImage?.cropped_image),
    resolveImageString(legacyProfileImage),
    resolveImageString(state?.user_session?.user_profile?.profile_image),
    resolveImageString(state?.user_session?.user_profile_image),
    resolveImageString(state?.user_session?.user_profile?.photo),
  ];

  const profileImageSource = profileImageCandidates.find(Boolean) || null;

  const profileType =
    state?.user_session?.user_type ??
    state?.user_session?.user_profile?.user_type ??
    "user";

  const handleChestClick = () => {
    if (progress >= 12 || stage !== 0) return;
    setProgress(progress + 1);
    setIsAnimating(false);
    setTimeout(() => setIsAnimating(true), 0);
    setTextAnimating(false);
    setTimeout(() => setTextAnimating(true), 0);
    setLastTapTime(Date.now());
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (stage === 0 && progress < 12 && Date.now() - lastTapTime > 1000 && progress > 0) {
        setProgress((prev) => Math.max(prev - 0.1, 0));
      }
    }, 50);
    return () => clearInterval(interval);
  }, [lastTapTime, progress, stage]);

  useEffect(() => {
    if (progress >= 12 && stage === 0) {
      setStage(1);
      setTimeout(() => {
        setTimeout(() => {
          setStage(2);
        }, 300);
      }, 1000);
    }
  }, [progress, stage]);

  useEffect(() => {
    const resolvedTheme = getFrameThemeById(collectorFrameThemeId);
    if (!resolvedTheme) return;
    setSelectedFrameTheme((currentTheme) =>
      currentTheme?.id === resolvedTheme.id ? currentTheme : resolvedTheme
    );
  }, [collectorFrameThemeId]);

  const handleChestFadeOutEnd = () => {
    setTimeout(() => {
      setShowCollectorFrame(true);
    }, 120);

    setTimeout(() => {
      setShowReceivedText(true);
      setShowCollectorsBadgeText(true);
      setShowRewardDetails(true);
    }, 900);
  };

  // Add a navigate function for the "Close" button
  const handleClose = () => {
    navigate('/profile');  // Navigate to the profile page when clicked
  };

  const handleThemeSelect = (theme) => {
    setSelectedFrameTheme(theme);
    setClaimError(null);
    if (hasClaimedFrame && theme.id !== collectorFrameThemeId) {
      setHasClaimedFrame(false);
    }
  };

  const handleClaimReward = async () => {
    if (isClaimingFrame || hasClaimedFrame || !selectedFrameTheme?.id) return;
    setIsClaimingFrame(true);
    setClaimError(null);

    try {
      const { response, data } = await fetchWrapper.post(
        REWARD_FRAME_ENDPOINT,
        {
          injectToken: true,
          body: { frame_theme: selectedFrameTheme.id },
        }
      );

      if (!response.ok) {
        throw new Error(data?.error_message || t('errors.claim_failed'));
      }

      if (data?.user_session) {
        stateHandler.set("user_session", data.user_session);
      } else if (data?.collector_frame_theme) {
        stateHandler.set(
          "user_session.user_flags.collector_frame_theme",
          data.collector_frame_theme
        );
      }

      setHasClaimedFrame(true);
    } catch (error) {
      console.error("Collector frame claim failed", error);
      setClaimError(
        typeof error === "string"
          ? error
          : error?.message || t('errors.claim_unavailable')
      );
    } finally {
      setIsClaimingFrame(false);
    }
  };

  const videoURL = 'https://video.files.suarte.art/background-mobile.mp4';

  return (
    <div className="page-container">

      <video className="background-video" autoPlay loop muted playsInline>
        <source src={videoURL} type="video/mp4" />
        {t('video_not_supported')}
      </video>

      {/* Render the "Tap the chest!" text unless progress is full */}
      {progress < 12 && (
        <h2 className={`tap-text ${textAnimating ? 'text-bounce' : ''} ${stage >= 1 ? 'tap-text-hidden' : ''}`}>
          <span className="border-text">{t('tap_chest')}</span>
        </h2>
      )}

      {/* Apply shaking animation to Closed Chest when stage is 1 */}
      {(stage !== 2) && (
        <img 
          src={CLOSED_CHEST_URL} 
          alt={t('alt.closed_chest')} 
          className={`closed-chest ${stage === 2 ? 'chest-fade-out' : stage === 1 ? 'chest-shake' : isAnimating ? 'chest-stretch-bounce' : ''}`} 
          onClick={handleChestClick} 
        />
      )}

      {/* Open Chest with Stellar Entrance */}
      {stage === 2 && (
        <img 
          src={OPEN_CHEST_URL} 
          alt={t('alt.open_chest')} 
          className="open-chest chest-fade-in"
          onAnimationEnd={handleChestFadeOutEnd} 
        />
      )}

      {/* Collector Frame Icon - emerges after chest animation */}
      {showCollectorFrame && (
        <div className="collector-reward-content">
          <div className={`collector-reward-details ${showRewardDetails ? 'is-visible' : ''}`}>
            {/* "You have received:" text */}
            {showReceivedText && (
              <p className="received-text">{t('received')}</p>
            )}

            {/* "Collector's Badge" text with animation */}
            {showCollectorsBadgeText && (
              <p className="collectors-badge-text animated-shine">{t('collector_badge')}</p>
            )}

            <p className="collector-frame-hint">
              {t('color_hint')}
            </p>

            <div className="collector-frame-theme-selector">
              {FRAME_THEMES.map((theme) => {
                const isActive = theme.id === selectedFrameTheme.id;
                const commonProps = {
                  className: `collector-frame-theme-option ${isActive ? 'is-active' : ''}`,
                  style: {
                    borderColor: theme.base,
                    backgroundColor: isActive ? theme.base : 'transparent',
                  },
                  role: 'button',
                  tabIndex: 0,
                  'aria-pressed': isActive,
                  'aria-label': t('aria.select_frame', { frame: theme.label }),
                  onClick: () => handleThemeSelect(theme),
                  onKeyDown: (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleThemeSelect(theme);
                    }
                  },
                };
                return (
                  <div key={theme.id} {...commonProps} />
                );
              })}
            </div>
          </div>

          <div className="collector-frame-stack">
            <UserProfileImage
              image={profileImageSource}
              typeOfProfile={profileType}
              className="collector-profile-image collector-frame-animated"
            />
            <CollectorFrameIcon
              className="collector-badge-icon collector-frame-animated"
              style={{
                '--collector-frame-color': selectedFrameTheme.base,
                '--collector-frame-color-darker': selectedFrameTheme.darker,
              }}
            />
          </div>

          <div className="collector-reward-actions">
            <button
              type="button"
              onClick={handleClaimReward}
              className={`claim-reward-action-button ${showRewardDetails ? 'is-visible' : ''}`}
              disabled={isClaimingFrame || hasClaimedFrame}
            >
              {hasClaimedFrame
                ? t('actions.claimed')
                : isClaimingFrame
                  ? t('actions.claiming')
                  : t('actions.claim_frame')}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className={`claim-reward-close-button ${showRewardDetails ? 'is-visible' : ''}`}
            >
              {t('actions.exit')}
            </button>
          </div>
          {claimError && (
            <p className="collector-frame-claim-error">
              {claimError}
            </p>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div className={`progress-bar-container ${stage >= 2 ? 'progress-bar-fade-out' : ''}`}>
        <div 
          className="progress-bar-fill" 
          style={{ width: `${(progress / 12) * 100}%` }}
        ></div>
      </div>
    </div>
  );
}
